<template>
  <v-menu :disabled="disabled" :close-on-content-click="false" v-model="show">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs" v-on="on"
        class="mt-2 mx-n2 px-2"
        style="height: unset;"
        text color="primary"
        :disabled="disabled"
        :loading="loadTimezonesStatus.pending"
      >
        <span
          :key="datetime?.getTime()"
          class="text--label-active"
          style="color: inherit; text-transform: none; letter-spacing: normal;"
        >
          {{getLocalName(value)}}
        </span>
        <v-icon v-if="!disabled" class="menu-down" v-html="mdiMenuDown" />
      </v-btn>
    </template>
    <div class="white d-flex flex-column" style="width: min(380px, 100vw - 24px); height: 508px; c: calc(100vh - 24px);">

      <div class="d-flex align-center white pa-4">
        <v-text-field
          class="white mr-2"
          autofocus dense hide-details outlined clearable persistent-placeholder
          :placeholder="(filtered.timezones.length > 1) ? `Closest ${countryCode ? (countryCode + ' ') : ''}city` : ''"
          :disabled="filtered.timezones.length <= 1"
          :append-icon="filter ? null : mdiMagnify"
          v-model="filter"
        />
        <v-menu offset-x>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" class="mr-n2 px-1" text height="40" :disabled="loadTimezonesStatus.pending">
              <div v-if="country.code" class="d-flex align-center justify-center" style="width: 24px;">
                <country-flag style="transform: scale(.862); margin: 0 -120px;" :country="country.code" />
              </div>
              <v-icon v-else v-html="mdiEarth" />
              <v-icon style="transform: rotate(-90deg);" v-html="mdiMenuDown" />
            </v-btn>
          </template>
          <div class="white d-flex flex-column" style="width: min(380px, 100vw - 24px); height: calc(100vh - 24px);">

            <div class="white pa-4">
              <v-text-field
                autofocus dense hide-details outlined clearable persistent-placeholder placeholder="Country"
                :append-icon="filter ? null : mdiMagnify"
                v-model="countryFilter"
              />
            </div>
            <v-list class="mt-n2 pt-0 overflow-auto" dense>
              <v-list-item
                v-for="c, i in countries" :key="c.code"
                v-if="(i == 0) || !countryFilter || c.name.toLowerCase().startsWith(countryFilter.toLowerCase())"
                class="simple py-3"
                :input-value="countryCode == c.code"
                @click="countryCode = c.code"
              >
                <country-flag v-if="c.code" class="my-n1" style="flex: none; font-size: 1.5em; line-height: inherit;" :country="c.code" />
                <v-icon v-else style="margin-right: -1px !important;" color="primary" v-html="mdiEarth" />
                <div v-bind:class="{ 'primary--text': !c.code }" v-html="c.name" />
              </v-list-item>
            </v-list>
          </div>
        </v-menu>
      </div>

      <v-list class="mt-n2 pt-0 overflow-auto" dense>

        <template v-if="filtered.timezones.length > 1">
          <v-list-item
            v-for="city in filtered.cities" :key="city.city + city.countryName"
            class="simple py-2"
            :input-value="city.timezone == value"
            @click="$emit('input', city.timezone); show = false;"
          >
            <div>
              <v-list-item-title v-text="city.city" />
              <div v-if="city.countryName" class="text--label-active">{{city.countryName}}</div>
              <div class="text--label-active">{{getLocalName(city.timezone)}}</div>
            </div>
          </v-list-item>

          <v-list-item
            v-if="country.cities.length"
            class="simple py-2 font-italic text--label-active"
            v-bind:class="{ 'mt-3': filtered.cities.length }"
            style="color: rgba(0, 0, 0, 0.6) !important;"
          >
            <template v-if="filtered.cities.length">Showing {{filtered.cities.length}} of {{country.cities.length}} known {{countryCode ? (countryCode + ' ') : ''}}cities</template>
            <template v-else>No matching cities found</template>
          </v-list-item>

          <v-spacer />

          <v-list-item
            class="mt-16 simple py-2 font-italic text--label-active"
            style="color: rgba(0, 0, 0, 0.6) !important;"
            @click="showAllTimezones = !showAllTimezones"
          >
            <div class="flex-grow-1">All {{countryCode ? (countryCode + ' ') : ''}}time zone locations</div>
            <v-icon
              class="my-n1"
              v-bind:style="{ transform: showAllTimezones ? null : 'rotate(-90deg)' }"
              v-html="mdiMenuDown"
            />
          </v-list-item>
        </template>

        <v-expand-transition>
          <div v-if="showAllTimezones || (filtered.timezones.length == 1)">
            <v-list-item
              v-for="timezone in filtered.timezones" :key="timezone"
              class="simple py-2"
              :input-value="timezone == value"
              @click="$emit('input', timezone); show = false;"
            >
              <div>
                <v-list-item-title v-text="timezone" />
                <div class="text--label-active">{{getLocalName(timezone)}}</div>
              </div>
            </v-list-item>
          </div>
        </v-expand-transition>

      </v-list>

      <v-spacer />

    </div>
  </v-menu>
</template>


<script>
import CountryFlag from '@/components/CountryFlag.vue';
import COUNTRIES from '@/utils/countries.js';
import { COUNTRY_CODE } from '@/utils/intl.js';
import { format, getTimezoneName, getOrdinalSuffixEN } from '@/utils/time.js';
import { mdiEarth, mdiMagnify, mdiMenuDown } from '@mdi/js';


function getCityKey(city) {
  return city.countryName + ' ' + (city.featured || 'Z') + ' ' + city.city;
}


export default {
  name: 'input-timezone',

  components: {
    CountryFlag
  },

  props: {
    value: String,
    datetime: Date,
    disabled: Boolean
  },

  data() {
    return {
      show: false,
      showAllTimezones: false,
      filter: null,
      countryFilter: null,
      timezones: [],
      countries: [],
      countryCode: COUNTRY_CODE,
      loadTimezonesStatus: this.$track('loadTimezones')
    };
  },

  created() {
    Object.assign(this, { mdiEarth, mdiMagnify, mdiMenuDown });
  },

  computed: {
    country() {
      return this.countries.find(x => x.code == this.countryCode) || { code: COUNTRY_CODE, cities: [], timezones: [] };
    },

    nCities() {
      return this.country.cities.length;
    },

    filtered() {
      const key = (this.filter || '').toLowerCase();
      if (!key)
        return { cities: this.country.cities.filter(x => x.featured), timezones: this.country.timezones };
      const cities = [];
      for (const city of this.country.cities)
        if (city.cityLower.startsWith(key))
          if (cities.push(city) == 100)
            break;
      return { cities, timezones: this.country.timezones };
    }
  },

  methods: {
    getLocalName(tz) {
      if (!tz)
        return 'Timezone';
      const d = this.datetime || new Date();
      const short = getTimezoneName(d, 'short', tz, 'en-US');
      const long = getTimezoneName(d, 'long', tz, 'en-US');
      return long.startsWith('GMT+') ? short : (long + ' (' + short + ')');
    },

    async loadTimezones() {
      const timezones = Intl.supportedValuesOf('timeZone');

      const cities = (await import('@/utils/city-country-timezone.json')).default;
      const byCountry = {};
      for (const [ city, country, timezone, featured ] of cities) {
        if (!timezone)
          continue;
        if (!byCountry[country])
          byCountry[country] = { timezones: [], cities: [] };
        byCountry[country].timezones.add(timezone);
        byCountry[country].cities.push({ city, cityLower: city.toLowerCase(), timezone, featured });
        // timezones.remove(timezone);
      }

      for (const [ code, { cities } ] of Object.entries(byCountry))
        cities.sort((a, b) => getCityKey(a).localeCompare(getCityKey(b)));

      // Our cities are not representative enough to capture all the timezones in a country
      const timezonesByCountry = (await import('@/utils/country-timezone.json')).default;
      for (const [ country, countryTimezones ] of Object.entries(timezonesByCountry)) {
        if (!byCountry[country])
          byCountry[country] = { timezones: [], cities: [] };
        for (const timezone of countryTimezones) {
          byCountry[country].timezones.add(timezone);
          // timezones.remove(timezone);
        }
      }

      const countries = COUNTRIES.map(([ name, code ]) => ({ name, code, ...byCountry[code] })).filter(x => x.cities);

      const allCities = [];
      for (const { name, cities } of countries)
        for (const city of cities)
          allCities.push({ ...city, countryName: name });
      allCities.sort((a, b) => getCityKey(a).localeCompare(getCityKey(b)));

      // If the selected timezone belongs to one and only one country, then we can select that country
      const matches = [];
      for (const { code, timezones } of countries)
        if (timezones.includes(this.value))
          matches.push(code);
      if (matches.length == 1)
        this.countryCode = matches[0];
      else if (matches.includes(COUNTRY_CODE))
        this.countryCode = COUNTRY_CODE;
      else
        this.countryCode = null;

      const i = countries.findIndex(x => x.code == COUNTRY_CODE);
      const local = countries[i];
      countries.splice(i, 1);

      this.timezones = timezones;
      this.countries = [ { name: 'All countries', code: null, timezones, cities: allCities }, local, ...countries ];
    }
  },

  watch: {
    show(v) {
      if (v)
        this.loadTimezones();
    },

    country(v) {
      if ((v.timezones.length == 1) && (this.value != v.timezones[0])) {
        this.$emit('input', v.timezones[0]);
        this.show = false;
      }
    }
  }
}
</script>
